import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, Card, CardContent, Tooltip as MuiTooltip, ToggleButton, ToggleButtonGroup, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TableSortLabel,
  TextField, InputAdornment
} from "@mui/material";
import { Info } from "lucide-react";
import { PiFileCsv } from "react-icons/pi";
import { AiOutlineSearch } from 'react-icons/ai';

const dummyData = {
  usa: 50,
  canada: 30,
  germany: 40,
};

function Chart({ servicesData = { descreapacy_by_country: dummyData, grouped_discrepancy_by_country: dummyData } }) {
  const [data, setData] = useState([]);
  const [viewType, setViewType] = useState('grouped');

  // New table state
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('discrepancy');

  useEffect(() => {
    fetchData();
  }, [servicesData, viewType]);

  const fetchData = () => {
    const dataKey = viewType === 'grouped' ? 'grouped_discrepancy_by_country' : 'descreapacy_by_country';
    const chartData = servicesData[dataKey];
    const hasData = chartData && Object.keys(chartData).length > 0;
    if (hasData) {
      let tmp = Object.keys(chartData).map((el, index) => {
        return {
          id: index,
          country: el,
          discrepancy: parseFloat(parseFloat(chartData[el]).toFixed(2)),
        };
      });

      setData(tmp);
    } else {
      setData([]);
    }
  };

  const handleExport = () => {
    if (!data || data.length === 0) return;
    
    const headers = ["Country", "Discrepancy Amount"];
    const csvRows = [headers.join(",")];
    
    data.forEach(row => {
      const country = `"${row.country}"`;
      const discrepancy = row.discrepancy;
      csvRows.push([country, discrepancy].join(","));
    });
    
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${viewType}_discrepancy_by_country.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Table filtering and sorting
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data;
    const q = searchQuery.toLowerCase();
    return data.filter((row) => 
      String(row.country).toLowerCase().includes(q) ||
      String(row.discrepancy).toLowerCase().includes(q)
    );
  }, [data, searchQuery]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, order, orderBy]);

  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const headCells = [
    { id: 'country', label: 'Country', sortable: true },
    { id: 'discrepancy', label: 'Discrepancy', sortable: true },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Card
        sx={{
          boxShadow: 3,
          borderRadius: 3,
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <CardContent sx={{ p: 0, flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 0, borderBottom: '1px solid #e5e7eb' }}>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="#1e293b"
                sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                Discrepancy by Country
                <MuiTooltip
                  title={
                    <div style={{ padding: '4px', maxWidth: '300px' }}>
                      <div style={{ marginBottom: '6px', fontSize: '12px', fontWeight: 400 }}>
                        Visualizes the distribution of total discrepancy amounts across different countries.
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: 400, color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                        Verification: Go to Document → Invoice, export the discrepancies list, group the rows by country, and sum the total discrepancy values to match the table rows.
                      </div>
                    </div>
                  }
                  arrow
                  placement="top"
                >
                  <div style={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                    <Info size={18} color="#666" />
                  </div>
                </MuiTooltip>
              </Typography>
              <Typography variant="caption" color="#94a3b8">
                {filteredData.length} countr{filteredData.length !== 1 ? 'ies' : 'y'} found
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px', height: '36px' } }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AiOutlineSearch size={16} color="#94a3b8" /></InputAdornment>,
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#f1f5f9',
                  borderRadius: '20px',
                  padding: '2px',
                }}
              >
                <Box
                  onClick={() => setViewType('grouped')}
                  sx={{
                    padding: '4px 12px',
                    borderRadius: '18px',
                    fontSize: '0.75rem',
                    fontWeight: viewType === 'grouped' ? 600 : 400,
                    color: viewType === 'grouped' ? '#fff' : '#64748b',
                    backgroundColor: viewType === 'grouped' ? '#7C2EA0' : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                  }}
                >
                  Grouped
                </Box>
                <Box
                  onClick={() => setViewType('individual')}
                  sx={{
                    padding: '4px 12px',
                    borderRadius: '18px',
                    fontSize: '0.75rem',
                    fontWeight: viewType === 'individual' ? 600 : 400,
                    color: viewType === 'individual' ? '#fff' : '#64748b',
                    backgroundColor: viewType === 'individual' ? '#7C2EA0' : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                  }}
                >
                  Individual
                </Box>
              </Box>
              
              <MuiTooltip title="Download CSV" placement="top" arrow>
                <span
                  onClick={data.length > 0 ? handleExport : undefined}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#f5f5f5",
                    cursor: data.length > 0 ? "pointer" : "default",
                    opacity: data.length > 0 ? 1 : 0.5,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (data.length > 0) {
                      e.currentTarget.style.backgroundColor = "#e0e0e0";
                    }
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  <PiFileCsv size={18} color="#333" />
                </span>
              </MuiTooltip>
            </Box>
          </Box>
          
          <Box sx={{ width: "100%", flexGrow: 1, position: "relative" }}>
            <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column' }}>
              <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {headCells.map((cell) => (
                        <TableCell
                          key={cell.id}
                          sx={{
                            fontWeight: 700, fontSize: '11px', backgroundColor: '#f8fafc',
                            color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em',
                            py: 1.5, whiteSpace: 'nowrap',
                          }}
                          sortDirection={orderBy === cell.id ? order : false}
                        >
                          <MuiTooltip title={cell.label} arrow placement="top">
                            <TableSortLabel
                              active={orderBy === cell.id}
                              direction={orderBy === cell.id ? order : 'asc'}
                              onClick={() => handleSort(cell.id)}
                            >
                              {cell.label}
                            </TableSortLabel>
                          </MuiTooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            '&:hover': { backgroundColor: '#f8fafc' },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <TableCell sx={{ fontSize: '12px', py: 1.5, fontWeight: 500, paddingLeft: '24px' }}>
                            {row.country}
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              fontSize: '12px', 
                              py: 1.5, 
                              fontWeight: 600,
                              color: row.discrepancy < 0 ? '#dc2626' : 'inherit',
                              paddingLeft: '24px'
                            }}
                          >
                            USD {row.discrepancy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                          No data found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={sortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{ borderTop: '1px solid #e5e7eb', flexShrink: 0 }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Chart;
